import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth'
import { doc, setDoc } from 'firebase/firestore'
import { auth, db } from '../firebase'

const AuthContext = createContext(null)

const googleProvider = new GoogleAuthProvider()
const facebookProvider = new FacebookAuthProvider()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  // Bumped after profile writes — Auth mutates currentUser in place, so setUser alone
  // may not re-render consumers when the object reference is unchanged.
  const [profileVersion, setProfileVersion] = useState(0)

  useEffect(() => {
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser)
      setLoading(false)
    })
  }, [])

  const value = useMemo(() => ({
    user,
    loading,
    uid: user?.uid ?? null,
    displayName: user?.displayName ?? null,
    email: user?.email ?? null,
    photoURL: user?.photoURL ?? null,
    isAuthenticated: Boolean(user),

    loginWithEmail: (email, password) =>
      signInWithEmailAndPassword(auth, email, password),

    registerWithEmail: async ({ email, password, displayName }) => {
      const credential = await createUserWithEmailAndPassword(auth, email, password)
      if (displayName) {
        await updateProfile(credential.user, { displayName })
      }
      // Preserve previous react-redux-firebase profile write
      await setDoc(
        doc(db, 'users', credential.user.uid),
        { email, displayName: displayName || null },
        { merge: true }
      )
      return credential
    },

    loginWithProvider: (providerName) => {
      const provider = providerName === 'facebook' ? facebookProvider : googleProvider
      return signInWithPopup(auth, provider)
    },

    resetPassword: (email) => sendPasswordResetEmail(auth, email),

    updateDisplayName: async (displayName) => {
      if (!user) {
        throw new Error('Not signed in')
      }
      const nextName = displayName?.trim() ? displayName.trim() : null
      await updateProfile(user, { displayName: nextName })
      await setDoc(
        doc(db, 'users', user.uid),
        { email: user.email, displayName: nextName },
        { merge: true }
      )
      await user.reload()
      setUser(auth.currentUser)
      setProfileVersion((version) => version + 1)
    },

    logout: () => signOut(auth),
  }), [user, loading, profileVersion])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
